import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@assetron/common/contexts/localization/LocalizationContext';
// project import
import Config from '@assetron/common/config';
import { RecaptchaProvider } from '@assetron/web-common/contexts/RecaptchaContext';
import ThemeCustomization from '@assetron/web-common/themes';
import { captureAttributionFromUrl } from '@assetron/common/utils/attributionUtils';
import {
  initializeGaExceptionTracking,
  initializeGoogleAnalytics,
  initializePageViewTracking,
  setUserDetails,
  trackUserSource,
} from '@assetron/common/utils/googleAnalytics';
import { getAssetronUserIdFromJwtPayload } from '@assetron/common/utils/identityClaims';
import { AuthWebProvider } from '@assetron/common/contexts/AuthWebContext';
import useAuth from '@assetron/common/hooks/useAuth';
import { useInitializeSentry } from '@assetron/common/config/sentryConfig';
import ErrorBoundary from '@assetron/common/components/ErrorBoundary/ErrorBoundary';
import { initializeAppInsights } from '@assetron/common/telemetry/appInsights';
import { createLogger } from '@assetron/common/telemetry/logger';
import ToasterContainer from '@assetron/web-common/components/Toaster/ToasterContainer';
import { GoogleOneTapProvider } from './contexts/GoogleOneTapContext';
import { InviteValidationProvider } from './contexts/InviteValidationContext';
import { RouteContextProvider } from './contexts/RouteContext';
import useInviteValidation from './hooks/useInviteValidation';
import './global.css';

const queryClient = new QueryClient();
const appLogger = createLogger('app.auth');

function AttributionCapture() {
  const { isUserAuthenticated } = useAuth();

  useEffect(() => {
    if (!isUserAuthenticated) {
      captureAttributionFromUrl();
    }
  }, [isUserAuthenticated]);

  return null;
}

function AuthGaIdentitySync() {
  const { companyInfo } = useAuth();
  const userId = getAssetronUserIdFromJwtPayload(companyInfo);

  useEffect(() => {
    setUserDetails(userId, null);
  }, [userId]);

  return null;
}

function AuthWebProviderWrapper({
  children,
}: Readonly<{
  children: React.ReactElement;
}>) {
  const { isInviteValidating } = useInviteValidation();
  return (
    <AuthWebProvider shouldNavigate={!isInviteValidating}>
      <>
        <AttributionCapture />
        <AuthGaIdentitySync />
        {children}
      </>
    </AuthWebProvider>
  );
}

export default function App() {
  useInitializeSentry({ appName: 'auth' });
  const { RECAPTCHA_SITE_KEY, GA_AUTH_TRACKING_ID } = Config;

  useEffect(() => {
    initializeAppInsights();
    appLogger.info('bootstrap_started');
    appLogger.info('bootstrap_completed', {
      hasGoogleAnalytics: Boolean(GA_AUTH_TRACKING_ID),
    });
  }, [GA_AUTH_TRACKING_ID]);

  useEffect(() => {
    if (!GA_AUTH_TRACKING_ID) {
      return undefined;
    }

    const initialized = initializeGoogleAnalytics(GA_AUTH_TRACKING_ID);
    if (!initialized) {
      return undefined;
    }

    trackUserSource();
    const teardownPageViewTracking = initializePageViewTracking();
    const teardownExceptionTracking = initializeGaExceptionTracking();

    return () => {
      teardownPageViewTracking?.();
      teardownExceptionTracking?.();
    };
  }, [GA_AUTH_TRACKING_ID]);

  return (
    <ErrorBoundary>
      <ThemeCustomization>
        <LocalizationProvider>
          <RecaptchaProvider siteKey={RECAPTCHA_SITE_KEY}>
            <InviteValidationProvider>
              <AuthWebProviderWrapper>
                <GoogleOneTapProvider>
                  <QueryClientProvider client={queryClient}>
                    <RouteContextProvider />
                    <ToasterContainer />
                  </QueryClientProvider>
                </GoogleOneTapProvider>
              </AuthWebProviderWrapper>
            </InviteValidationProvider>
          </RecaptchaProvider>
        </LocalizationProvider>
      </ThemeCustomization>
    </ErrorBoundary>
  );
}
